/**
 * How a long list of people is cut down before it is shown.
 *
 * Pure and platform-agnostic, so it lives here rather than in either app: a
 * threshold kept in two files drifts the first time one side is tuned.
 */

/** Rows show this many before the rest collapse behind the toggle. */
export const COLLAPSED_PARTICIPANT_COUNT = 5;

/**
 * Collapse only once it hides at least this many. Below it the toggle takes
 * about as much room as the people it removes, so the row gets no shorter and
 * the reader loses names for nothing.
 */
export const MIN_HIDDEN_TO_COLLAPSE = 3;

export type SplitParticipants<T> = {
  /** The people to render right now. */
  visible: T[];
  /** How many `visible` leaves out — 0 whenever the whole list is shown. */
  hiddenCount: number;
  /** Whether this list is long enough to earn a toggle at all. */
  collapsible: boolean;
};

/**
 * Split a list into what to show and how many that leaves out.
 *
 * `collapsible` is about the list's length alone, not the current state, so a
 * caller can decide whether to render a toggle without re-deriving the rule
 * while expanded.
 */
export const splitParticipants = <T>(
  items: readonly T[],
  expanded: boolean,
): SplitParticipants<T> => {
  const collapsible = items.length - COLLAPSED_PARTICIPANT_COUNT >= MIN_HIDDEN_TO_COLLAPSE;

  if (!collapsible || expanded) {
    return { visible: [...items], hiddenCount: 0, collapsible };
  }

  return {
    visible: items.slice(0, COLLAPSED_PARTICIPANT_COUNT),
    hiddenCount: items.length - COLLAPSED_PARTICIPANT_COUNT,
    collapsible,
  };
};
